import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderSlot as _renderSlot, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass, resolveDirective as _resolveDirective, openBlock as _openBlock, createElementBlock as _createElementBlock, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = ["id"]

import { onMounted, onUnmounted, onUpdated, reactive, computed } from 'vue'
import { useEventBus } from '@vueuse/core'
import { gsap } from 'gsap'
import { ScrollTrigger } from '@/assets/js/ScrollTrigger.min.js'
import ScrollEngine from '../general/ScrollEngine.vue'
import { MJPSection } from '../magicjohnsonpark/types'
import useSectionData from '@/composables/mjp/useSectionData'
import usePreviousSection from '@/composables/mjp/usePreviousSection'
import useNextSection from '@/composables/mjp/useNextSection'
import useScrollOffset from '@/composables/useScrollOffset'
import { ImageBackgroundState, ScrollOffset } from './types'
import { HotSpotData } from './types'
import useSectionIndex from '@/composables/mjp/useSectionIndex'
import ScrollProgress from './ScrollProgress.vue'

interface Props {
  currentSection: string
  currentPage: number
  thisSection: string
  currentSectionHasLoaded: boolean
  activeHotspotId?: string
  forward: boolean
  hotspots?: Array<HotSpotData>
  hotspotRefs?: object
  position: string
  disableScrollSnap?: boolean
  disableKeyNavigation?: boolean
}

interface SectionState {
  scrollProgress: number
  narrativeScrollProgress: number
  responsiveScrollOffset: ScrollOffset
  prefersReducedMotion: boolean
}


export default /*#__PURE__*/_defineComponent({
  __name: 'SectionBase',
  props: {
    currentSection: null,
    currentPage: null,
    thisSection: null,
    currentSectionHasLoaded: { type: Boolean },
    activeHotspotId: null,
    forward: { type: Boolean },
    hotspots: null,
    hotspotRefs: null,
    position: null,
    disableScrollSnap: { type: Boolean },
    disableKeyNavigation: { type: Boolean }
  },
  setup(__props: any, { expose }) {

const props = __props as {
  currentSection: string
  currentPage: number
  thisSection: string
  currentSectionHasLoaded: boolean
  activeHotspotId?: string
  forward: boolean
  hotspots?: Array<HotSpotData>
  hotspotRefs?: object
  position: string
  disableScrollSnap?: boolean
  disableKeyNavigation?: boolean
}

gsap.registerPlugin(ScrollTrigger)

// this component handles automated scroll and animation
// mechanics within a section


const sectionChangedBus = useEventBus('section-changed')
const popoversNeedUpdateBus = useEventBus('popovers-need-update')
let timeline
let lastRefreshTime = Date.now()
const windowSize = {
  x: window.innerWidth,
  y: window.innerHeight,
}
let state: SectionState = reactive({
  scrollProgress: 0,
  narrativeScrollProgress: 0,
  responsiveScrollOffset: {
    proxyPosition: '',
    offsetX: 0,
  },
  prefersReducedMotion: matchMedia('(prefers-reduced-motion)').matches,
})

const imageBackgroundState: ImageBackgroundState = reactive({
  translate: '',
  scale: '',
  scaleRatio: 1,
  hotspotSize: 0,
  naturalWidth: 1920,
  naturalHeight: 1080,
  proxyPosition: '',
  offsetX: 0,
})

expose({ state })

const sectionData = computed(() => {
  return useSectionData(props.thisSection)
})

// calculate the scaling to keep the hotspot container
// lined up with the background image
const calculateZoom = () => {
  imageBackgroundState.scaleRatio = Math.max(
    windowSize.x / 1920, // used to be naturalWidth / height
    windowSize.y / 1080
  )
}

// when one or more hotspots are occluded, we change the
// viewing mode to scroll horizontally across the background,
// ensuring that the user sees all of the hotspots

// this method calculates the horizontal offset needed to
// keep the hotspot container aligned with the scrolling background

const calculateScrollOffset = () => {
  state.responsiveScrollOffset = useScrollOffset(
    props.position,
    imageBackgroundState,
    props.hotspots,
    props.hotspotRefs,
    state.narrativeScrollProgress, // float progress measured in pages
    windowSize
  )
  popoversNeedUpdateBus.emit()
}

const getToggleActionsForElement = (selector) => {
  const element = document.querySelector(selector)
  if (element.classList.contains('one-way')) {
    return 'play none none none'
  } else {
    return 'play reverse play reverse'
  }
}

const addTextCardAnimation = (pageNum, screenHeightStart, screenHeightEnd) => {
  const textCardSelector = `#${props.currentSection}-${
    pageNum + 1
  } > .text-card`
  const sectionScreenHeight = screenHeightEnd - screenHeightStart
  const textCard = document.querySelector(textCardSelector)
  if (textCard) {
    const sectionSelector = `#${props.thisSection}-scroller`
    const startDelay = 0.25
    // fade the text card in and out
    gsap.fromTo(
      textCardSelector,
      {
        opacity: 0,
        display: 'none',
      },
      {
        opacity: 1,
        display: 'inherit',
        duration: 0.5,
        scrollTrigger: {
          trigger: sectionSelector + ' > div',
          scroller: sectionSelector,
          start: `top ${(screenHeightStart + startDelay) * -100}%`,
          end: `top ${screenHeightEnd * -100}%`,
          toggleActions: getToggleActionsForElement(textCardSelector),
        },
      }
    )
    // slide the text card up as the user scrolls
    if (!state.prefersReducedMotion) {
      timeline.fromTo(
        textCardSelector,
        { y: 50 },
        { y: -25, duration: sectionScreenHeight },
        screenHeightStart
      )
    }
    // hold to end of timeline
    timeline.to(
      textCardSelector,
      {
        y: -25,
        duration: 0,
      },
      computedScreenHeights.value - 1
    )
  } else {
    timeline.to(null, { duration: 1 }, pageNum)
  }
}

const addAnimationsForPage = (pageNum, screenHeightStart, screenHeightEnd) => {
  // we do this because gsap is logging a warning even when its target exists
  gsap.config({
    nullTargetWarn: false,
  })
  addTextCardAnimation(pageNum, screenHeightStart, screenHeightEnd)
  //addExtendedTextCardAnimation(pageNum, screenHeightStart, screenHeightEnd)
}

const addPageAnimations = () => {
  if (sectionData.value) {
    let screenHeight = 0
    // scale the timings down since the actual timeline is one screen height
    // taller than the specified values
    //let ratio = (computedScreenHeights.value - 1) / computedScreenHeights.value
    let ratio = 1
    for (let i = 0; i < sectionData.value.pageMap.length; i++) {
      addAnimationsForPage(
        i,
        screenHeight * ratio,
        screenHeight + pageMap.value[i] * ratio
      )
      screenHeight += pageMap.value[i]
    }
  }
}

const setupScroller = () => {
  const sectionSelector = `#${props.thisSection}-scroller`

  ScrollTrigger.addEventListener('refreshInit', () => {
    lastRefreshTime = Date.now()
  })

  timeline = gsap.timeline({
    scrollTrigger: {
      trigger: sectionSelector + ' > div',
      scroller: sectionSelector,
      start: 'top top',
      end: 'bottom bottom',
      scrub: 0.5,
      toggleActions: 'play none none reverse',
      onLeaveBack: () => {
        if (
          Date.now() - lastRefreshTime > 1000 &&
          useSectionIndex(props.thisSection) > 0
        ) {
          sectionChangedBus.emit({
            section: usePreviousSection(props.thisSection),
            pageNumber: 0,
            forward: !sectionData.value.goBackwardsToPreviousSection,
            lastSection: props.currentSection,
          })
        }
      },
      onUpdate: (self) => {
        // this assumes that the last page of any section is the one
        // to get the responsive scrolling
        state.scrollProgress = self.progress
        state.narrativeScrollProgress =
          calculatedPage(self.progress) - (pageMap.value.length - 1)
        calculateScrollOffset()
        let newCurrentPage = Math.floor(calculatedPage(self.progress))
        if (newCurrentPage != props.currentPage) {
          sectionChangedBus.emit({
            section: props.thisSection,
            pageNumber: newCurrentPage,
            forward: newCurrentPage > props.currentPage,
            lastSection: props.currentSection,
          })
        }
      },
      onLeave: () => {
        if (
          Date.now() - lastRefreshTime > 1000 &&
          useNextSection(props.thisSection) != MJPSection.None
        ) {
          sectionChangedBus.emit({
            section: useNextSection(props.thisSection),
            pageNumber: 0,
            forward: true,
            lastSection: props.currentSection,
          })
        }
      },
    },
  })
}

const handleResize = () => {
  updateWindowSize()
  calculateScrollOffset()
  calculateZoom()
}

const updateWindowSize = () => {
  windowSize.x = window.innerWidth
  windowSize.y = window.innerHeight
}

const calculatedPage = (rawProgress) => {
  const currentPageMap = pageMap.value
  if (computedScreenHeights.value) {
    const pageUnit = 1.0 / (computedScreenHeights.value - 1)
    const calculatedUnit = rawProgress / pageUnit
    let heightSum = 0
    if (Array.isArray(currentPageMap)) {
      for (let i = 0; i < currentPageMap.length; i++) {
        if (
          calculatedUnit >= heightSum &&
          calculatedUnit < heightSum + currentPageMap[i]
        ) {
          const fractionalAmount =
            (calculatedUnit - heightSum) / currentPageMap[i]
          return i + fractionalAmount
        }
        heightSum += currentPageMap[i]
      }
    }
  }
  return currentPageMap.length
}

const pageMap = computed(() => {
  return sectionData.value.pageMap
})

const computedScreenHeights = computed(() => {
  const currentPageMap = pageMap.value
  if (Array.isArray(currentPageMap)) {
    // we add one to the sum to make sure to account for
    // starting at one screen height instead of zero
    return currentPageMap.reduce((partialSum, a) => partialSum + a, 0) + 1
  } else {
    return 0
  }
})

onUpdated(() => {
  calculateScrollOffset()
  calculateZoom()
})

onMounted(() => {
  handleResize()
  setupScroller()
  addPageAnimations()
})

onUnmounted(() => {
  timeline.kill()
})

return (_ctx: any,_cache: any) => {
  const _directive_resize = _resolveDirective("resize")!

  return _withDirectives((_openBlock(), _createElementBlock("div", {
    id: `${props.thisSection}-section`,
    class: _normalizeClass(["absolute left-0 top-0 w-full h-full flex justify-center items-center", {
      invisible:
        props.currentSection == props.thisSection &&
        !props.currentSectionHasLoaded,
    }])
  }, [
    _createVNode(ScrollEngine, {
      id: `${props.thisSection}-scroller`,
      ref: "scroller",
      currentSection: props.currentSection,
      currentPage: props.currentPage,
      thisSection: props.thisSection,
      currentSectionHasLoaded: props.currentSectionHasLoaded,
      activeHotspotId: props.activeHotspotId,
      forward: props.forward,
      screenHeights: _unref(computedScreenHeights),
      pageMap: _unref(pageMap),
      disableScrollSnap: props.disableScrollSnap,
      disableKeyNavigation: props.disableKeyNavigation
    }, {
      default: _withCtx(() => [
        _renderSlot(_ctx.$slots, "default"),
        _renderSlot(_ctx.$slots, "interactive")
      ]),
      _: 3
    }, 8, ["id", "currentSection", "currentPage", "thisSection", "currentSectionHasLoaded", "activeHotspotId", "forward", "screenHeights", "pageMap", "disableScrollSnap", "disableKeyNavigation"]),
    _createVNode(ScrollProgress, {
      scrollProgress: _unref(state).scrollProgress
    }, null, 8, ["scrollProgress"])
  ], 10, _hoisted_1)), [
    [_directive_resize, handleResize]
  ])
}
}

})